import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { getCurrentUserId, getMyStudentId } from "../../services/api";
import Layout from "../../components/Layout/Layout";
import useAgentTracker from "../../hooks/useAgentTracker";
import { triggerAnalyticsRefresh } from "../../utils/analyticsRefresh";
import "./quiz.css";
import useLearnerSnapshot from "../../hooks/useLearnerSnapshot";
import "./quiz-page.css";

// JSS subjects first, in a sensible order. `value` is what the API receives
// (kept identical to the previous values where they existed).
const JSS_SUBJECTS = [
  { value: "Mathematics", label: "Mathematics" },
  { value: "English", label: "English" },
  { value: "Basic Science", label: "Basic Science" },
  { value: "Basic Technology", label: "Basic Technology" },
  { value: "Social Studies", label: "Social Studies" },
  { value: "Civic Education", label: "Civic Education" },
  { value: "Business Studies", label: "Business Studies" },
  { value: "Agricultural Science", label: "Agricultural Science" },
  {
    value: "Information Technology",
    label: "Computer Studies / Information Technology",
  },
  { value: "Home Economics", label: "Home Economics" },
  {
    value: "Christian Religious Studies",
    label: "Christian Religious Studies (CRS)",
  },
  { value: "IRS", label: "Islamic Religious Studies (IRS)" },
  { value: "French", label: "French" },
  {
    value: "Physical and Health Education",
    label: "Physical and Health Education",
  },
  { value: "Cultural and Creative Arts", label: "Cultural and Creative Arts" },
  { value: "History", label: "History" },
];

// Other subjects kept available, shown after a divider.
const OTHER_SUBJECTS = [
  "Biology",
  "Chemistry",
  "Physics",
  "Economics",
  "Account",
  "Government",
  "Literature in English",
  "Drawing Practices",
].map((s) => ({ value: s, label: s }));

const ALL_SUBJECTS = [...JSS_SUBJECTS, ...OTHER_SUBJECTS];

const grades = ["JSS1", "JSS2", "JSS3"];

const TOPIC_SUGGESTIONS = {
  Mathematics: [
    "Whole Numbers",
    "Fractions",
    "Decimals",
    "Algebraic Expressions",
    "Simple Equations",
    "Angles",
    "Perimeter and Area",
    "Ratio and Proportion",
  ],
  English: [
    "Parts of Speech",
    "Tenses",
    "Comprehension",
    "Punctuation",
    "Vocabulary",
    "Letter Writing",
  ],
  "Basic Science": [
    "Living Things",
    "Energy",
    "Matter",
    "Human Body",
    "Environment",
  ],
  "Basic Technology": [
    "Safety in the Workshop",
    "Materials and Their Uses",
    "Drawing Instruments",
    "Simple Machines",
  ],
  "Social Studies": [
    "Family",
    "Culture",
    "Social Groups",
    "Our Environment",
  ],
  "Civic Education": [
    "Values",
    "Citizenship",
    "Rights and Duties",
    "National Symbols",
  ],
  "Business Studies": [
    "Office Practice",
    "Bookkeeping",
    "Commerce",
    "Keyboarding",
  ],
  "Agricultural Science": [
    "Meaning of Agriculture",
    "Farm Tools",
    "Crop Production",
    "Farm Animals",
  ],
  "Information Technology": [
    "Computer Hardware",
    "Computer Software",
    "Input and Output Devices",
    "Internet Safety",
  ],
};

// Match any incoming subject text (URL, profile) to a subject value.
const matchSubject = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim().toLowerCase();
  const found = ALL_SUBJECTS.find(
    (o) => o.value.toLowerCase() === s || o.label.toLowerCase() === s
  );
  if (found) return found.value;
  if (s === "maths" || s === "math") return "Mathematics";
  if (s.startsWith("english")) return "English";
  if (s.includes("computer") || s === "ict") return "Information Technology";
  if (s === "crs") return "Christian Religious Studies";
  if (s === "irs") return "IRS";
  return String(raw).trim();
};

// "JSS 1", "jss1", "Jss-2", "1" -> "JSS1"; anything else -> ""
const normalizeGrade = (raw) => {
  if (raw === undefined || raw === null) return "";
  const s = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, "");
  const m = s.match(/^(?:JSS|JS)?([123])$/);
  return m ? `JSS${m[1]}` : "";
};

const readStoredGrade = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return (
      normalizeGrade(u.grade) ||
      normalizeGrade(u.class) ||
      normalizeGrade(u.student_class) ||
      normalizeGrade(u.profile?.grade)
    );
  } catch {
    return "";
  }
};

const scoreMessage = (pct) => {
  if (pct >= 80) return "Excellent work!";
  if (pct >= 50) return "Good effort — nearly there";
  return "Keep practising — you'll get there";
};

function QuizAgent() {
  const location = useLocation();
  const [showForm, setShowForm] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingType, setLoadingType] = useState("generating");
  const [error, setError] = useState(null);
  const [forceRender, setForceRender] = useState(0);
  const [quizParams, setQuizParams] = useState(() => {
    const query = new URLSearchParams(location.search);
    const ctx = location.state?.context || {};
    return {
      subject: matchSubject(ctx.subject || query.get("subject") || ""),
      // Links may carry lowercase topics ("fractions"): show them in Title Case
      topic: (ctx.topic || query.get("topic") || "")
        .trim()
        .replace(/\b([a-z])/g, (c) => c.toUpperCase()),
      grade: normalizeGrade(query.get("grade")) || readStoredGrade(),
      numQuestions: "5",
    };
  });
  const [prefilled] = useState(() => {
    const query = new URLSearchParams(location.search);
    const ctx = location.state?.context || {};
    return Boolean(ctx.topic || query.get("topic"));
  });
  const [quizResults, setQuizResults] = useState(null);
  const [aiFeedback, setAiFeedback] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const navigate = useNavigate();
  const { snapshot } = useLearnerSnapshot();

  // Timer state for total quiz time
  const [totalQuizTime, setTotalQuizTime] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  // Current question index for navigation
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Agent tracking
  const sessionIdRef = useRef(null);
  const { startAgentSession, recordInteraction, endAgentSession } =
    useAgentTracker();

  // Pre-select the student's class from their profile if not already known
  useEffect(() => {
    if (quizParams.grade) return;
    let cancelled = false;
    api
      .get("/profile/me")
      .then((res) => {
        const g = normalizeGrade(res?.data?.grade || res?.data?.class);
        if (!cancelled && g) {
          setQuizParams((prev) => (prev.grade ? prev : { ...prev, grade: g }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timer effect for total quiz time with auto-submit
  useEffect(() => {
    let interval = null;

    if (isTimerActive && timeRemaining > 0 && !quizResults && !autoSubmitted) {
      interval = setInterval(() => {
        setTimeRemaining((prevTime) => {
          if (prevTime <= 1) {
            // Time's up - auto-submit the quiz
            console.log("Quiz time expired - auto-submitting...");
            setIsTimerActive(false);
            setAutoSubmitted(true);
            // Use setTimeout to ensure state is updated before auto-submit
            setTimeout(() => handleAutoSubmit(), 100);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, timeRemaining, quizResults, autoSubmitted]);

  // Initialize timer when quiz starts
  useEffect(() => {
    if (
      quiz &&
      quiz.questions &&
      quiz.questions.length > 0 &&
      !quizResults &&
      !autoSubmitted
    ) {
      // Set total quiz time based on number of questions
      const numQuestions = parseInt(quizParams.numQuestions);
      let totalTimeSeconds = 180; // default 3 minutes

      if (numQuestions <= 5) {
        totalTimeSeconds = 180; // 3 minutes for 5 questions
      } else if (numQuestions <= 10) {
        totalTimeSeconds = 300; // 5 minutes for 10 questions
      } else if (numQuestions <= 15) {
        totalTimeSeconds = 600; // 10 minutes for 15 questions
      } else {
        totalTimeSeconds = 900; // 15 minutes for 20 questions
      }

      setTotalQuizTime(totalTimeSeconds);
      setTimeRemaining(totalTimeSeconds);
      setIsTimerActive(true);
      setAutoSubmitted(false);
      setCurrentQuestionIndex(0);

      console.log(
        `Quiz timer set to ${totalTimeSeconds} seconds (${Math.floor(
          totalTimeSeconds / 60
        )}:${(totalTimeSeconds % 60)
          .toString()
          .padStart(2, "0")}) for ${numQuestions} questions`
      );
    }
  }, [quiz, quizParams.numQuestions, quizResults, autoSubmitted]);

  const handleAutoSubmit = async () => {
    if (autoSubmitted) {
      console.log("Auto-submit already in progress, skipping...");
      return;
    }

    console.log("Auto-submitting quiz due to time expiration...");
    setAutoSubmitted(true);
    setIsTimerActive(false);
    setLoading(true);
    setLoadingType("calculating");

    try {
      // Count answered questions
      const answeredCount = Object.keys(answers).filter(
        (key) => answers[key] && answers[key] !== "__AUTO_INCORRECT__"
      ).length;

      console.log(
        `Auto-submit: ${answeredCount} out of ${quiz.questions.length} questions answered`
      );

      // Handle fallback quiz
      if (quiz.quiz_id && quiz.quiz_id.startsWith("fallback_")) {
        setTimeout(() => {
          const correctAnswered = Math.floor(answeredCount * Math.random());
          const mockResults = {
            score: correctAnswered,
            total: quiz.questions.length,
            percentage: (correctAnswered / quiz.questions.length) * 100,
            questions: quiz.questions.map((q, i) => {
              const questionNum = i + 1;
              const wasAnswered = answers[`${questionNum}`];
              return {
                ...q,
                is_correct: wasAnswered ? Math.random() > 0.5 : false,
                explanation: wasAnswered
                  ? "This is a mock explanation."
                  : "Question not answered within the time limit.",
                auto_incorrect: !wasAnswered,
              };
            }),
            feedback: `Quiz auto-submitted due to time expiration. ${
              quiz.questions.length - answeredCount
            } unanswered questions were marked as incorrect.`,
          };
          setQuizResults(mockResults);
          setLoading(false);
        }, 1000);
        return;
      }

      // For real quiz submission, only send answered questions
      const submissionAnswers = {};
      Object.keys(answers).forEach((key) => {
        if (answers[key] && answers[key] !== "__AUTO_INCORRECT__") {
          submissionAnswers[key] = answers[key];
        }
      });

      try {
        const currentUserId = getCurrentUserId();
        const response = await api.post("/api/agents/quiz/submit", {
          quiz_id: quiz.quiz_id,
          answers: submissionAnswers,
          user_id: currentUserId,
          student_id: getMyStudentId(),
          auto_submitted: true,
          total_questions: quiz.questions.length,
          answered_questions: answeredCount,
        });

        console.log("Auto-submit successful:", response);

        let resultsData = response.data.results || response.data;

        // Ensure unanswered questions are marked as incorrect
        if (resultsData.questions) {
          resultsData.questions = resultsData.questions.map((q, i) => {
            const questionNum = i + 1;
            const wasAnswered =
              answers[`${questionNum}`] &&
              answers[`${questionNum}`] !== "__AUTO_INCORRECT__";

            if (!wasAnswered) {
              return {
                ...q,
                is_correct: false,
                explanation: "Question not answered within the time limit.",
                auto_incorrect: true,
              };
            }
            return q;
          });

          // Recalculate final score including unanswered questions as incorrect
          const correctAnswers = resultsData.questions.filter(
            (q) => q.is_correct
          ).length;
          resultsData.score = correctAnswers;
          resultsData.total = quiz.questions.length;
          resultsData.percentage =
            (correctAnswers / quiz.questions.length) * 100;
        }

        setQuizResults(resultsData);
        await fetchAIFeedback(resultsData, quiz);

        // Analytics and tracking
        triggerAnalyticsRefresh({
          studentId: getMyStudentId(),
          quizId: quiz.quiz_id,
          subject: quizParams.subject,
          topic: quizParams.topic,
          score: resultsData.score,
          total: resultsData.total,
        });

        if (sessionIdRef.current) {
          await recordInteraction(
            sessionIdRef.current,
            `Quiz: ${quizParams.subject} - ${quizParams.topic} (${quiz.questions.length} questions) - AUTO SUBMITTED`,
            `Score: ${resultsData.score}/${
              resultsData.total
            } (${resultsData.percentage.toFixed(1)}%) - Time expired`,
            null,
            null
          );
        }
      } catch (submitError) {
        console.error("Error auto-submitting quiz:", submitError);
        // Create fallback results
        const fallbackResults = {
          score: 0,
          total: quiz.questions.length,
          percentage: 0,
          questions: quiz.questions.map((q, i) => ({
            ...q,
            is_correct: false,
            explanation: "Question not answered within the time limit.",
            auto_incorrect: true,
          })),
          feedback:
            "Quiz auto-submitted due to time expiration. All questions marked as incorrect due to submission error.",
        };
        setQuizResults(fallbackResults);
      }
    } catch (err) {
      console.error("Auto-submit error:", err);
      setError(
        "Quiz auto-submitted but there was an error processing results."
      );
    } finally {
      setLoading(false);
      setShowForm(false);
      setForceRender((prev) => prev + 1);
    }
  };

  // Question navigation functions
  const handleNextQuestion = () => {
    if (!quiz || !quiz.questions || autoSubmitted) return;

    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < quiz.questions.length) {
      setCurrentQuestionIndex(nextIndex);
    } else if (nextIndex === quiz.questions.length) {
      // All questions completed, user can now submit
      console.log("All questions completed");
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0 && !autoSubmitted) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const fetchAIFeedback = async (results, quizData) => {
    setFeedbackLoading(true);
    try {
      // Get current user ID from JWT token
      const currentUserId = getCurrentUserId();
      const studentId = currentUserId || getMyStudentId() || "anonymous";

      console.log("Fetching feedback for student:", studentId);

      const response = await api.post("/api/agents/feedback/analyze", {
        student_id: studentId,
        quiz_id: quizData.quiz_id,
        action: "student_feedback",
        score: results.score,
        total: results.total,
        topic: quizParams.topic,
        subject: quizParams.subject,
      });

      if (response.data && response.data.success) {
        const feedbackData =
          response.data.student_feedback_result?.[0] || response.data.feedback;
        setAiFeedback(feedbackData);
        console.log("AI Feedback received:", feedbackData);
      }
    } catch (error) {
      console.error("Failed to fetch AI feedback:", error);
      // Set basic feedback as fallback
      const percentage = (results.score / results.total) * 100;
      setAiFeedback({
        message:
          percentage >= 80
            ? `Excellent work! You scored ${results.score}/${results.total}.`
            : percentage >= 60
            ? `Good effort! You scored ${results.score}/${results.total}. Keep practicing!`
            : `You scored ${results.score}/${results.total}. Don't give up - review the material and try again!`,
        strengths: ["Completed the quiz"],
        improvements: ["Review incorrect answers"],
        suggestions: "Keep practicing to improve your understanding.",
      });
    } finally {
      setFeedbackLoading(false);
    }
  };

  const generateQuiz = async () => {
    if (!quizParams.subject || !quizParams.topic || !quizParams.grade) {
      setError("Please choose a subject, a topic and your class.");
      return;
    }

    setLoading(true);
    setLoadingType("generating");
    setError(null);

    try {
      // Start agent tracking session
      const trackingSessionId = await startAgentSession(
        "quiz_agent",
        quizParams.subject,
        quizParams.topic
      );
      sessionIdRef.current = trackingSessionId;
      console.log("Quiz agent tracking session started:", trackingSessionId);
      const timeoutId = window.quizTimeoutId;
      if (timeoutId) clearTimeout(timeoutId);

      const numQuestionsValue = parseInt(quizParams.numQuestions) || 5;
      const currentUserId = getCurrentUserId();

      // Check if user is authenticated
      if (!currentUserId) {
        console.warn("No authenticated user found for quiz generation");
        setError("Please log in to generate quizzes");
        setLoading(false);
        return;
      }

      const consistentStudentId = getMyStudentId() || currentUserId || null;

      const payload = {
        subject: quizParams.subject,
        topic: quizParams.topic,
        grade: quizParams.grade,
        num_questions: numQuestionsValue,
        user_id: consistentStudentId,
        student_id: consistentStudentId,
      };

      console.log("Quiz generation payload:", payload);
      console.log("Making API call to generate AI quiz");

      const response = await api.post("/api/agents/quiz/generate", payload);
      console.log("FULL QUIZ RESPONSE:", response);

      // Extract quiz data
      const quizData = response.data.quiz || response.data;
      console.log("EXTRACTED QUIZ DATA:", quizData);

      // Validate quiz structure
      if (
        !quizData ||
        !quizData.questions ||
        !Array.isArray(quizData.questions)
      ) {
        console.error("Invalid quiz data format:", quizData);
        throw new Error("Invalid quiz data format received from server");
      }

      console.log("QUESTIONS COUNT:", quizData.questions.length);
      console.log("SAMPLE QUESTION:", quizData.questions[0]);

      // Store answers globally for debugging
      window.quizAnswers = {};

      // Force a complete reset
      setLoading(false);

      // Use a small delay to ensure state updates
      setTimeout(() => {
        console.log("Setting quiz data:", quizData);
        setQuiz(quizData);
        setShowForm(false);
        setForceRender((prev) => prev + 1);
        console.log(
          "Quiz should now be displayed with",
          quizData.questions?.length || 0,
          "questions"
        );
      }, 50);
    } catch (err) {
      console.error("QUIZ GENERATION ERROR:", err);

      // Show a friendly inline message (the backend returns a readable
      // `detail`, e.g. 503 "We couldn't create your quiz right now...").
      const detail = err.response?.data?.detail;
      let friendly;
      if (typeof detail === "string" && detail.trim()) {
        friendly = detail;
      } else if (
        !err.response &&
        (err.code === "ERR_NETWORK" ||
          /network|timeout/i.test(err.message || ""))
      ) {
        friendly =
          "We couldn't reach MyEduGenius. Check your internet connection and try again.";
      } else if (err.response.status === 429) {
        friendly =
          "Lots of students are making quizzes right now. Please try again in a moment.";
      } else {
        friendly =
          "We couldn't create your quiz right now. Please try again in a moment.";
      }
      setError(friendly);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answer, optIndex, questionIndex) => {
    const questionNum = questionIndex + 1;

    setAnswers((prev) => {
      const newAnswers = { ...prev };

      // Store answer by question number (string key) - this is what backend expects
      newAnswers[`${questionNum}`] = answer;

      console.log(
        `Question ${questionNum} (ID: ${questionId}): Selected "${answer}"`
      );
      return newAnswers;
    });
  };

  const handleSubmit = async () => {
    if (autoSubmitted) {
      console.log("Quiz already auto-submitted, ignoring manual submit");
      return;
    }

    // Stop the timer
    setIsTimerActive(false);

    setLoading(true);
    setLoadingType("calculating");

    try {
      // Format answers correctly - send full text answers with string keys
      const formattedAnswers = {};
      quiz.questions.forEach((question, index) => {
        const questionNum = index + 1;
        const answer = answers[`${questionNum}`];

        if (answer) {
          formattedAnswers[`${questionNum}`] = answer;
          console.log(`Submitting Q${questionNum}: "${answer}"`);
        }
      });

      console.log("Final formatted answers:", formattedAnswers);
      console.log("Current quiz ID being submitted:", quiz.quiz_id);
      console.log(
        "Current quiz questions being submitted:",
        quiz.questions.map((q) => ({
          question: q.question,
          answer: q.answer,
        }))
      );

      // Handle fallback quiz
      if (quiz.quiz_id && quiz.quiz_id.startsWith("fallback_")) {
        setTimeout(() => {
          const mockResults = {
            score: Math.floor(Math.random() * quiz.questions.length) + 1,
            total: quiz.questions.length,
            percentage: Math.random() * 100,
            questions: quiz.questions.map((q, i) => ({
              ...q,
              is_correct: Math.random() > 0.5,
              explanation: "This is a mock explanation.",
            })),
            feedback: "Good attempt!",
          };
          setQuizResults(mockResults);
          setLoading(false);
        }, 1000);
        return;
      }

      try {
        console.log("Submitting quiz with answers:", formattedAnswers);
        console.log("Quiz ID:", quiz.quiz_id);

        const currentUserId = getCurrentUserId();
        const response = await api.post("/api/agents/quiz/submit", {
          quiz_id: quiz.quiz_id,
          answers: formattedAnswers,
          user_id: currentUserId,
          student_id: getMyStudentId(),
        });

        console.log("Quiz submission successful:", response);

        // Extract results from response
        let resultsData;
        if (response.data.results) {
          resultsData = response.data.results;
          console.log("Found standard results format");
        } else if (response.data.success && response.data.results) {
          resultsData = response.data.results;
          console.log("Found nested results format");
        } else {
          resultsData = response.data;
          console.log("Using direct response data");
        }

        console.log("Extracted quiz results:", resultsData);

        // Enhanced debugging - check if explanations exist
        if (resultsData && resultsData.questions) {
          console.log(
            "Number of questions in results:",
            resultsData.questions.length
          );

          resultsData.questions.forEach((q, i) => {
            const hasExplanation = Boolean(q.explanation);
            const explanationType = typeof q.explanation;

            console.log(
              `Question ${i + 1} explanation present:`,
              hasExplanation
            );
            console.log(`Question ${i + 1} explanation type:`, explanationType);

            if (hasExplanation) {
              const isFallback =
                q.explanation ===
                "Quiz processing encountered an issue. This is an estimated result.";
              console.log(
                `Question ${i + 1} has fallback explanation:`,
                isFallback
              );
              console.log(
                `Question ${i + 1} explanation preview:`,
                `${q.explanation.substring(0, 100)}...`
              );
            } else {
              console.log(`Question ${i + 1} explanation MISSING`);
            }

            console.log(`Question ${i + 1} correct:`, q.is_correct);
            console.log(
              `Question ${i + 1} correct answer:`,
              q.correct_answer || "Not specified"
            );
          });
        }

        // Update state with results
        setQuizResults(resultsData);

        // Fetch AI-powered feedback
        await fetchAIFeedback(resultsData, quiz);

        // Trigger comprehensive analytics refresh
        triggerAnalyticsRefresh({
          studentId: getMyStudentId(),
          quizId: quiz.quiz_id,
          subject: quizParams.subject,
          topic: quizParams.topic,
          score: resultsData.score,
          total: resultsData.total,
        });
        console.log("Analytics refresh triggered after quiz submission");

        // Record the quiz interaction
        if (sessionIdRef.current) {
          try {
            const score = resultsData.score || 0;
            const total = resultsData.total || quiz.questions.length;
            const percentage = resultsData.percentage || (score / total) * 100;

            await recordInteraction(
              sessionIdRef.current,
              `Quiz: ${quizParams.subject} - ${quizParams.topic} (${total} questions)`,
              `Score: ${score}/${total} (${percentage.toFixed(1)}%)`,
              null, // duration will be calculated by the tracker
              null // no feedback for now
            );
            console.log("Quiz interaction recorded");
          } catch (error) {
            console.error("Failed to record quiz interaction:", error);
          }
        }

        setLoading(false);
        setShowForm(false);
        setForceRender((prev) => prev + 1);

        return;
      } catch (submitError) {
        console.error("Error submitting quiz:", submitError);
        console.error("Error response data:", submitError.response?.data);
      }

      // Fallback results if submission fails
      console.error(
        "Falling back to generated results due to submission error"
      );

      const fallbackResults = {
        score: Math.floor(Math.random() * quiz.questions.length) + 1,
        total: quiz.questions.length,
        percentage: Math.random() * 100,
        questions: quiz.questions.map((q, i) => {
          const isCorrect = Math.random() > 0.5;

          let fallbackExplanation;
          if (q.options && q.options.length > 0) {
            const randomAnswerIndex = Math.floor(
              Math.random() * q.options.length
            );
            const randomAnswer = q.options[randomAnswerIndex];
            fallbackExplanation = `The answer is likely "${randomAnswer}" based on general knowledge.`;
          } else {
            fallbackExplanation =
              "This question requires specific knowledge about the topic.";
          }

          return {
            ...q,
            is_correct: isCorrect,
            explanation: fallbackExplanation,
            questionIndex: i,
          };
        }),
        feedback:
          "Quiz results may not be accurate due to a processing issue. Please try again.",
      };

      setQuizResults(fallbackResults);
      setError(
        "Note: Quiz results may not be fully accurate due to a submission issue."
      );
      setShowForm(false);
    } catch (err) {
      console.error("Quiz submission error:", err);
      setError("Failed to submit quiz. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetQuiz = () => {
    setShowForm(true);
    setQuiz(null);
    setAnswers({});
    setError(null);
    setQuizResults(null);
    setAiFeedback(null);
    setFeedbackLoading(false);
    // Keep subject / topic / grade so the student can quickly try again
    setQuizParams((prev) => ({ ...prev }));
    // Reset timer state
    setCurrentQuestionIndex(0);
    setTotalQuizTime(0);
    setTimeRemaining(0);
    setIsTimerActive(false);
    setAutoSubmitted(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const answeredCount = quiz?.questions
    ? quiz.questions.filter((_, i) => answers[`${i + 1}`]).length
    : 0;

  // Leave the current quiz (asks first if it's in progress)
  const handleNewQuiz = () => {
    const inProgress = quiz && !quizResults && answeredCount > 0;
    if (
      inProgress &&
      !window.confirm("Leave this quiz? Your answers will be lost.")
    ) {
      return;
    }
    resetQuiz();
  };

  const handleDownload = () => {
    if (!quizResults?.download_filename) return;
    import("../../utils/fileDownloader")
      .then((module) => {
        module.downloadFile(
          quizResults.download_filename,
          "Quiz Results",
          (errorMsg) => setError(errorMsg)
        );
      })
      .catch((err) => console.error("Error loading file downloader:", err));
  };

  const goToTutor = () => {
    navigate("/agents/tutor", {
      state: {
        context: { subject: quizParams.subject, topic: quizParams.topic },
      },
    });
  };

  // Is `option` (at optIndex) the correct answer for this question?
  const isCorrectOption = (option, optIndex, resultQuestion, question) => {
    const correct =
      resultQuestion?.correct_answer ??
      question?.correct_answer ??
      question?.answer;
    if (correct === undefined || correct === null || correct === "")
      return false;
    const c = String(correct).trim();
    if (c === String(option).trim()) return true;
    const letter = String.fromCharCode(65 + optIndex);
    return c.length === 1 && c.toUpperCase() === letter;
  };

  const handleInputChange = (field, value) => {
    setQuizParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatExplanation = (text) => {
    if (!text || text.trim() === "") return "No explanation available.";

    if (typeof text !== "string") {
      console.warn("Explanation is not a string type:", typeof text, text);
      text = String(text);
    }

    if (
      text ===
        "Quiz processing encountered an issue. This is an estimated result." ||
      text === "No explanation available."
    ) {
      return text;
    }

    if (text.startsWith("The correct answer is")) {
      const match = text.match(/The correct answer is ['"]([^'"]+)['"]\./);
      if (match && match[1]) {
        return `${match[1]} is the correct answer because it accurately describes the concept being asked about.`;
      }
    }

    console.log("Raw explanation before formatting:", JSON.stringify(text));

    return text
      .replace(/\\\(/g, "")
      .replace(/\\\)/g, "")
      .replace(/\$\$/g, "")
      .replace(/\$/g, "")
      .replace(/\\textm?\{([^}]*)\}/g, "$1")
      .replace(/\\text([a-z]*)\{([^}]*)\}/g, "$2")
      .replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, "($1)/($2)")
      .replace(/\\sqrt\{([^}]*)\}/g, "√($1)")
      .replace(/\\boxed\{([^}]*)\}/g, "Answer: $1")
      .replace(/\\sin/g, "sin")
      .replace(/\\cos/g, "cos")
      .replace(/\\tan/g, "tan")
      .replace(/\\log/g, "log")
      .replace(/^\s*([a-z])/, (match, p1) => p1.toUpperCase())
      .replace(/([^.!?])$/, "$1.");
  };

  // ---------- derived values for rendering ----------
  const isGenerating = loading && loadingType === "generating";
  const isCalculating = loading && loadingType === "calculating";
  const subjectLabel =
    ALL_SUBJECTS.find((s) => s.value === quizParams.subject)?.label ||
    quizParams.subject;
  const topicSuggestions = TOPIC_SUGGESTIONS[quizParams.subject] || [];
  const totalQ = quiz?.questions?.length || 0;
  const allAnswered = totalQ > 0 && answeredCount === totalQ;
  const isLastQuestion = currentQuestionIndex === totalQ - 1;
  const resultScore = quizResults ? Number(quizResults.score) || 0 : 0;
  const resultTotal = quizResults
    ? Number(quizResults.total) || totalQ || 0
    : 0;
  const resultPct = quizResults
    ? typeof quizResults.percentage === "number"
      ? quizResults.percentage
      : resultTotal
      ? (resultScore / resultTotal) * 100
      : 0
    : 0;
  const scoreBand = resultPct >= 80 ? "high" : resultPct >= 50 ? "mid" : "low";
  const timeLow = timeRemaining <= 60;
  const timePct = totalQuizTime
    ? Math.max(0, Math.min(100, (timeRemaining / totalQuizTime) * 100))
    : 0;
  const progressPct = totalQ
    ? ((currentQuestionIndex + 1) / totalQ) * 100
    : 0;

  const renderQuestionText = (question, index) =>
    question.question || question.text || `Question ${index + 1}`;

  // ---------- views ----------
  const renderForm = () => (
    <form
      className="qz-card qz-form"
      aria-labelledby="qz-form-title"
      onSubmit={(e) => {
        e.preventDefault();
        if (!isGenerating) generateQuiz();
      }}
      noValidate
    >
      <h2 id="qz-form-title" className="qz-card-title">
        Create your quiz
      </h2>
      <p className="qz-card-sub">
        Pick a subject and topic, and we'll make questions for your class.
      </p>

      {(snapshot?.focus || []).filter((f) => f.kind === "weak").length > 0 && (
        <div className="qz-recommend" aria-label="Recommended for you">
          <span className="qz-recommend-label">Recommended for you (topics you found hard):</span>
          <div className="qz-recommend-chips">
            {snapshot.focus
              .filter((f) => f.kind === "weak")
              .map((f) => (
                <button
                  key={`${f.subject}-${f.topic}`}
                  type="button"
                  className={`qz-recommend-chip ${
                    quizParams.topic.toLowerCase() === String(f.topic).toLowerCase() ? "on" : ""
                  }`}
                  onClick={() =>
                    setQuizParams((p) => ({ ...p, subject: matchSubject(f.subject) || p.subject, topic: f.topic }))
                  }
                >
                  {f.topic} · {f.subject} ({f.score}%)
                </button>
              ))}
          </div>
        </div>
      )}

      {quizParams.subject && quizParams.topic && (
        <div className="qz-ready" role="status">
          <span className="qz-ready-icon" aria-hidden="true">
            ✓
          </span>
          <span>
            Ready:{" "}
            <strong>
              {subjectLabel} · {quizParams.topic}
            </strong>
            {prefilled && (
              <span className="qz-ready-tip">
                {" "}
                — choose the number of questions and press Generate.
              </span>
            )}
          </span>
        </div>
      )}

      {error && (
        <div className="qz-alert qz-alert-error" role="alert">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="qz-field">
        <label htmlFor="qz-subject">Subject</label>
        <select
          id="qz-subject"
          value={quizParams.subject}
          onChange={(e) => handleInputChange("subject", e.target.value)}
          className="qz-input"
          required
        >
          <option value="">Choose a subject</option>
          <optgroup label="JSS subjects">
            {JSS_SUBJECTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Other subjects">
            {OTHER_SUBJECTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </optgroup>
          {quizParams.subject &&
            !ALL_SUBJECTS.some((s) => s.value === quizParams.subject) && (
              <option value={quizParams.subject}>{quizParams.subject}</option>
            )}
        </select>
      </div>

      <div className="qz-field">
        <label htmlFor="qz-topic">Topic</label>
        <input
          type="text"
          id="qz-topic"
          value={quizParams.topic}
          onChange={(e) => handleInputChange("topic", e.target.value)}
          placeholder="e.g. Fractions, Parts of Speech, Energy"
          className="qz-input"
          autoComplete="off"
          required
        />
        {topicSuggestions.length > 0 && (
          <div className="qz-chips" aria-label="Suggested topics">
            <span className="qz-chips-label">Try:</span>
            {topicSuggestions.map((t) => (
              <button
                type="button"
                key={t}
                className={`qz-chip ${
                  quizParams.topic === t ? "is-active" : ""
                }`}
                aria-pressed={quizParams.topic === t}
                onClick={() => handleInputChange("topic", t)}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="qz-field-row">
        <div className="qz-field">
          <label htmlFor="qz-grade">Class</label>
          <select
            id="qz-grade"
            value={quizParams.grade}
            onChange={(e) => handleInputChange("grade", e.target.value)}
            className="qz-input"
            required
          >
            <option value="">Choose your class</option>
            {grades.map((grade) => (
              <option key={grade} value={grade}>
                {grade}
              </option>
            ))}
          </select>
        </div>

        <div className="qz-field">
          <label htmlFor="qz-num">Number of questions</label>
          <select
            id="qz-num"
            value={quizParams.numQuestions}
            onChange={(e) => handleInputChange("numQuestions", e.target.value)}
            className="qz-input"
          >
            <option value="5">5 questions · 3 min</option>
            <option value="10">10 questions · 5 min</option>
            <option value="15">15 questions · 10 min</option>
            <option value="20">20 questions · 15 min</option>
          </select>
        </div>
      </div>

      <button
        type="submit"
        className="eg-btn eg-btn-primary eg-btn-lg qz-generate"
        disabled={isGenerating}
        aria-busy={isGenerating}
      >
        {isGenerating ? (
          <>
            <span className="qz-spinner" aria-hidden="true" />
            Creating your quiz…
          </>
        ) : (
          "Generate quiz"
        )}
      </button>
    </form>
  );

  const renderTaking = () => {
    const question = quiz.questions[currentQuestionIndex];
    const questionNum = currentQuestionIndex + 1;
    return (
      <>
        <div className="qz-card qz-progress">
          <div className="qz-progress-row">
            <span className="qz-progress-label">
              Question {questionNum} of {totalQ}
            </span>
            <span
              className={`qz-timer ${timeLow ? "is-low" : ""}`}
              role="timer"
              aria-label={`Time left ${formatTime(timeRemaining)}`}
            >
              <span aria-hidden="true">⏱</span>
              <span className="qz-timer-text">
                {formatTime(timeRemaining)}
              </span>
              <span className="qz-timer-unit">left</span>
            </span>
          </div>
          <div
            className="qz-progress-track"
            role="progressbar"
            aria-label="Quiz progress"
            aria-valuemin={1}
            aria-valuemax={totalQ}
            aria-valuenow={questionNum}
          >
            <div
              className="qz-progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="qz-time-track" aria-hidden="true">
            <div
              className="qz-time-fill"
              style={{ width: `${timePct}%` }}
            />
          </div>
          <div className="qz-dots" aria-label="Jump to question">
            {quiz.questions.map((_, i) => (
              <button
                type="button"
                key={i}
                className={`qz-dot ${
                  i === currentQuestionIndex ? "is-current" : ""
                } ${answers[`${i + 1}`] ? "is-done" : ""}`}
                onClick={() => !autoSubmitted && setCurrentQuestionIndex(i)}
                aria-label={`Question ${i + 1}${
                  answers[`${i + 1}`] ? ", answered" : ""
                }`}
                aria-current={i === currentQuestionIndex ? "step" : undefined}
              >
                {i + 1}
              </button>
            ))}
          </div>
          {timeLow && !autoSubmitted && (
            <p className="qz-timer-note">
              Less than a minute left — your quiz will be submitted
              automatically when the time runs out.
            </p>
          )}
        </div>

        {autoSubmitted && (
          <div className="qz-alert qz-alert-info" role="status">
            Time's up — your quiz was submitted automatically. Unanswered
            questions count as incorrect.
          </div>
        )}

        {error && (
          <div className="qz-alert qz-alert-error" role="alert">
            {error}
          </div>
        )}

        <section
          key={currentQuestionIndex}
          className="qz-card qz-question"
          aria-labelledby={`qz-q-${questionNum}`}
        >
          <p className="qz-q-number">Question {questionNum}</p>
          <h3 id={`qz-q-${questionNum}`} className="qz-q-text">
            {renderQuestionText(question, currentQuestionIndex)}
          </h3>

          <div className="qz-options">
            {question.options.map((option, optIndex) => {
              const isSelected = answers[`${questionNum}`] === option;
              const letter = String.fromCharCode(65 + optIndex);
              return (
                <button
                  type="button"
                  key={optIndex}
                  className={`qz-option ${isSelected ? "is-selected" : ""}`}
                  aria-pressed={isSelected}
                  onClick={() =>
                    handleAnswerSelect(
                      question.id || question.q_no || questionNum,
                      option,
                      optIndex,
                      currentQuestionIndex
                    )
                  }
                >
                  <span className="qz-option-letter" aria-hidden="true">
                    {isSelected ? "✓" : letter}
                  </span>
                  <span className="qz-option-text">
                    <span className="qz-sr-only">{letter}. </span>
                    {option}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <div className="qz-nav">
          <button
            type="button"
            className="eg-btn eg-btn-outline"
            onClick={handlePreviousQuestion}
            disabled={currentQuestionIndex === 0}
          >
            ← Previous
          </button>
          {isLastQuestion ? (
            <button
              type="button"
              className="eg-btn eg-btn-accent"
              onClick={handleSubmit}
              disabled={!allAnswered || loading}
            >
              Submit quiz
            </button>
          ) : (
            <button
              type="button"
              className="eg-btn eg-btn-primary"
              onClick={handleNextQuestion}
              disabled={!answers[`${questionNum}`]}
            >
              Next →
            </button>
          )}
        </div>
        <p className="qz-hint">
          {isLastQuestion && !allAnswered
            ? `Answer every question to submit — ${answeredCount} of ${totalQ} answered.`
            : isLastQuestion
            ? "All questions answered. Press Submit when you're ready."
            : "Choose an answer, then press Next."}
        </p>
      </>
    );
  };

  const renderFeedback = () => (
    <>
      {feedbackLoading && (
        <div className="qz-card qz-feedback qz-feedback-loading" role="status">
          <span className="qz-spinner" aria-hidden="true" />
          Getting personalised feedback for you…
        </div>
      )}

      {aiFeedback && !feedbackLoading && (
        <section className="qz-card qz-feedback" aria-label="AI feedback">
          <h2 className="qz-section-title">
            <span aria-hidden="true">🤖</span> Personalised feedback
          </h2>
          {aiFeedback.message && (
            <p className="qz-feedback-msg">{aiFeedback.message}</p>
          )}

          {aiFeedback.strengths && aiFeedback.strengths.length > 0 && (
            <div className="qz-fb-block qz-fb-good">
              <h3>✅ Your strengths</h3>
              <ul>
                {aiFeedback.strengths.map((strength, idx) => (
                  <li key={idx}>{strength}</li>
                ))}
              </ul>
            </div>
          )}

          {aiFeedback.improvements && aiFeedback.improvements.length > 0 && (
            <div className="qz-fb-block qz-fb-warn">
              <h3>📈 Things to work on</h3>
              <ul>
                {aiFeedback.improvements.map((improvement, idx) => (
                  <li key={idx}>{improvement}</li>
                ))}
              </ul>
            </div>
          )}

          {aiFeedback.suggestions && aiFeedback.suggestions.length > 0 && (
            <div className="qz-fb-block qz-fb-info">
              <h3>💡 Suggestions</h3>
              {Array.isArray(aiFeedback.suggestions) ? (
                <ul>
                  {aiFeedback.suggestions.map((suggestion, idx) => (
                    <li key={idx}>{suggestion}</li>
                  ))}
                </ul>
              ) : (
                <p>{aiFeedback.suggestions}</p>
              )}
            </div>
          )}
        </section>
      )}
    </>
  );

  const renderResults = () => (
    <>
      <section
        className={`qz-card qz-summary qz-band-${scoreBand}`}
        aria-labelledby="qz-summary-score"
      >
        <p className="qz-summary-eyebrow">Your result</p>
        <p id="qz-summary-score" className="qz-score">
          {resultScore} / {resultTotal}
          <span className="qz-score-sep" aria-hidden="true">
            ·
          </span>
          <span className="qz-score-pct">{Math.round(resultPct)}%</span>
        </p>
        <p className="qz-summary-msg">{scoreMessage(resultPct)}</p>

        <div className="qz-score-track" aria-hidden="true">
          <div
            className="qz-score-fill"
            style={{ width: `${Math.max(0, Math.min(100, resultPct))}%` }}
          />
        </div>

        {quizResults.already_submitted ? (
          <p className="qz-points qz-points-muted">
            Already submitted — no new points
          </p>
        ) : quizResults.points_earned ? (
          <p className="qz-points">
            🎉 <strong>+{quizResults.points_earned} points</strong> earned
          </p>
        ) : null}

        {autoSubmitted && (
          <p className="qz-summary-note">
            Time ran out, so your quiz was submitted automatically.
          </p>
        )}
        {quizResults.feedback && (
          <p className="qz-summary-note">{quizResults.feedback}</p>
        )}
        {error && (
          <div className="qz-alert qz-alert-error" role="alert">
            {error}
          </div>
        )}

        <div className="qz-summary-actions">
          <button
            type="button"
            className="eg-btn eg-btn-primary"
            onClick={resetQuiz}
          >
            Try another quiz
          </button>
          <button
            type="button"
            className="eg-btn eg-btn-outline"
            onClick={goToTutor}
          >
            Practise with AI Tutor
          </button>
          {quizResults.download_filename && (
            <button
              type="button"
              className="eg-btn eg-btn-outline"
              onClick={handleDownload}
            >
              Download results
            </button>
          )}
          <button
            type="button"
            className="eg-btn eg-btn-outline"
            onClick={() => navigate("/student-dashboard")}
          >
            Back to dashboard
          </button>
        </div>
      </section>

      {renderFeedback()}

      <section className="qz-review" aria-labelledby="qz-review-title">
        <h2 id="qz-review-title" className="qz-section-title">
          Review your answers
        </h2>
        {quiz.questions.map((question, index) => {
          const resultQuestion = quizResults.questions?.[index];
          const userAnswer = answers[`${index + 1}`];
          const gotIt = Boolean(resultQuestion?.is_correct);
          const correctText =
            resultQuestion?.correct_answer ||
            question?.correct_answer ||
            question?.answer ||
            "Not available";

          return (
            <article
              key={index}
              className={`qz-card qz-review-item ${
                resultQuestion ? (gotIt ? "is-right" : "is-wrong") : ""
              }`}
            >
              <div className="qz-review-head">
                <p className="qz-q-number">Question {index + 1}</p>
                {resultQuestion && (
                  <span
                    className={`qz-badge ${
                      gotIt ? "qz-badge-right" : "qz-badge-wrong"
                    }`}
                  >
                    {gotIt ? "✓ Correct" : "✗ Incorrect"}
                  </span>
                )}
              </div>
              <h3 className="qz-q-text">
                {renderQuestionText(question, index)}
              </h3>

              <ul className="qz-options qz-options-review">
                {question.options.map((option, optIndex) => {
                  const isSelected = userAnswer === option;
                  const isCorrect = resultQuestion
                    ? isCorrectOption(
                        option,
                        optIndex,
                        resultQuestion,
                        question
                      ) ||
                      (isSelected && gotIt)
                    : false;
                  const isWrongPick =
                    isSelected && Boolean(resultQuestion) && !isCorrect;
                  const letter = String.fromCharCode(65 + optIndex);
                  const state = isCorrect
                    ? "is-correct"
                    : isWrongPick
                    ? "is-wrong"
                    : isSelected
                    ? "is-selected"
                    : "";
                  return (
                    <li key={optIndex} className={`qz-option ${state}`}>
                      <span className="qz-option-letter" aria-hidden="true">
                        {isCorrect ? "✓" : isWrongPick ? "✗" : letter}
                      </span>
                      <span className="qz-option-text">
                        <span className="qz-sr-only">{letter}. </span>
                        {option}
                      </span>
                      {(isCorrect || isSelected) && (
                        <span className="qz-option-tag">
                          {isCorrect && isSelected
                            ? "Your answer"
                            : isCorrect
                            ? "Correct answer"
                            : "Your answer"}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>

              {!userAnswer && resultQuestion && (
                <p className="qz-hint qz-hint-left">
                  You didn't answer this one in time.
                </p>
              )}

              {resultQuestion && (
                <div className="qz-explain">
                  <p>
                    <strong>Explanation: </strong>
                    {formatExplanation(
                      resultQuestion.explanation || "No explanation available."
                    )}
                  </p>
                  {!gotIt && (
                    <p className="qz-explain-answer">
                      The correct answer is: <strong>{correctText}</strong>
                    </p>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </section>
    </>
  );

  const renderQuiz = () => (
    <div className="qz-quiz">
      <div className="qz-card qz-quiz-bar">
        <div className="qz-quiz-meta">
          <h2 className="qz-quiz-title">
            {subjectLabel} · {quizParams.topic}
          </h2>
          <p className="qz-quiz-info">
            {quizParams.grade} · {totalQ} questions · {formatTime(totalQuizTime)}{" "}
            total
          </p>
        </div>
        {!quizResults && (
          <button
            type="button"
            className="eg-btn eg-btn-outline qz-btn-sm"
            onClick={handleNewQuiz}
          >
            New quiz
          </button>
        )}
      </div>

      {quiz && quiz.questions && quiz.questions.length > 0 ? (
        quizResults ? (
          renderResults()
        ) : (
          renderTaking()
        )
      ) : (
        <div className="qz-card qz-empty" role="alert">
          <h3 className="qz-card-title">We couldn't load this quiz</h3>
          <p className="qz-card-sub">
            No questions came back for this topic. Try a different topic or
            try again in a moment.
          </p>
          <button
            type="button"
            className="eg-btn eg-btn-primary"
            onClick={resetQuiz}
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Layout fullBleed>
      <div className="quiz-page">
        <div className="qz-shell">
          <header className="qz-page-header">
            <div>
              <p className="qz-eyebrow">Practice</p>
              <h1 className="qz-title">Quiz</h1>
              <p className="qz-subtitle">
                Test yourself on any topic and see how you're doing.
              </p>
            </div>
            <button
              type="button"
              className="eg-btn eg-btn-outline qz-btn-sm qz-back"
              onClick={() => navigate("/student-dashboard")}
            >
              ← Dashboard
            </button>
          </header>

          {isCalculating ? (
            <div className="qz-card qz-loading" role="status">
              <span className="qz-spinner qz-spinner-lg" aria-hidden="true" />
              <p className="qz-loading-title">Marking your answers…</p>
              <p className="qz-card-sub">This only takes a moment.</p>
            </div>
          ) : showForm ? (
            renderForm()
          ) : (
            renderQuiz()
          )}
        </div>
      </div>
    </Layout>
  );
}

export default QuizAgent;
